import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Settings, Award, Download, Share2, ExternalLink, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipientWallet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('recipient_token');
    if (!token) {
      navigate("/recipient/auth");
      return;
    }
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('recipient_token');
      const response = await axios.get(`${API}/recipient/credentials?token=${token}`);
      setCredentials(response.data);
    } catch (err) {
      toast.error("Failed to load credentials");
      navigate("/recipient/auth");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (serialNumber) => {
    window.open(`${API}/download/${serialNumber}/pdf`, '_blank');
    toast.success("Downloading PDF...");
  };

  const downloadPNG = (serialNumber) => {
    window.open(`${API}/download/${serialNumber}/png`, '_blank');
    toast.success("Downloading PNG...");
  };

  const shareLinkedIn = async (serialNumber, title) => {
    await axios.post(`${API}/track/${serialNumber}/linkedin-share`);
    const cacheBuster = Date.now();
    const url = encodeURIComponent(`${BACKEND_URL}/api/og/verify/${serialNumber}?v=${cacheBuster}`);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    toast.success("Opening LinkedIn...");
  };

  const addToLinkedInProfile = async (serialNumber, title, createdAt, orgName) => {
    const certUrl = encodeURIComponent(`${window.location.origin}/verify/${serialNumber}`);
    const certName = encodeURIComponent(title);
    const organizationName = encodeURIComponent(orgName);
    
    const issueDate = new Date(createdAt);
    const issueYear = issueDate.getFullYear();
    const issueMonth = issueDate.getMonth() + 1;
    
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certName}&organizationName=${organizationName}&issueYear=${issueYear}&issueMonth=${issueMonth}&certUrl=${certUrl}&certId=${serialNumber}`;
    
    window.open(linkedInUrl, '_blank');
    toast.success("✨ Add to your LinkedIn profile for celebration animation!");
  };

  const handleLogout = () => {
    localStorage.removeItem('recipient_token');
    navigate("/recipient/auth");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="wallet-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50" data-testid="wallet-nav">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>My Credentials</span>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-slate-600" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-16" data-testid="wallet-content">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Your Credentials</h1>
          <p className="text-slate-600">{credentials.length} certificates earned</p>
        </div>

        {credentials.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center" data-testid="no-credentials">
            <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>No Credentials Yet</h2>
            <p className="text-slate-600">You haven't received any certificates yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((cred, idx) => (
              <div 
                key={cred.recipient.id} 
                className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover-lift smooth-transition"
                data-testid={`credential-card-${idx}`}
              >
                {/* Certificate Preview */}
                <div className="certificate-preview guilloche-bg p-8">
                  <div className="text-center">
                    <h3 className="text-xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Certificate</h3>
                    <p className="text-sm text-slate-600 mb-4">{cred.recipient.name}</p>
                    <p className="text-sm font-semibold text-slate-900">{cred.certificate.title}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-1">Issued by</p>
                    <p className="text-sm font-medium text-slate-900">{cred.issuer.organization_name}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-1">Date</p>
                    <p className="text-sm text-slate-600">{cred.recipient.completion_date}</p>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs text-slate-500 mb-1">Serial</p>
                    <code className="text-xs text-slate-900 font-mono bg-slate-100 px-2 py-1 rounded">{cred.recipient.serial_number}</code>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <Button
                      onClick={() => window.open(`${BACKEND_URL}/verify/${cred.recipient.serial_number}`, '_blank')}
                      className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg py-2 text-sm"
                      data-testid={`view-btn-${idx}`}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Certificate
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => downloadPDF(cred.recipient.serial_number)}
                        variant="outline"
                        className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg py-2 text-sm"
                        data-testid={`pdf-btn-${idx}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        onClick={() => downloadPNG(cred.recipient.serial_number)}
                        variant="outline"
                        className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg py-2 text-sm"
                        data-testid={`png-btn-${idx}`}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PNG
                      </Button>
                    </div>
                    <Button
                      onClick={() => shareLinkedIn(cred.recipient.serial_number, cred.certificate.title)}
                      variant="outline"
                      className="w-full bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg py-2 text-sm"
                      data-testid={`linkedin-btn-${idx}`}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Share Post
                    </Button>
                    <Button
                      onClick={() => addToLinkedInProfile(
                        cred.recipient.serial_number, 
                        cred.certificate.title,
                        cred.recipient.created_at,
                        cred.issuer.organization_name
                      )}
                      className="w-full bg-[#0A66C2] text-white hover:bg-[#004182] rounded-lg py-2 text-sm mt-2"
                      data-testid={`add-linkedin-profile-btn-${idx}`}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Add to LinkedIn Profile
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientWallet;
